/**
 * QueuedSortableView — the Queue segment's queued elements as drag-to-order
 * lists, one per layer (Paths / Landers / Offers). The order is the priority;
 * the top of each list is the NEXT UP. Replaces the old 1–10 dropdown table.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { ALL_LAYERS, type QueueElement } from '@trafficpulse/shared';

import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';
import type { BulkAwareProps } from '../../bulk/row/bulk-row.types';
import { compareQueueOrder } from '../../queue-filters';
import QueuedLayerSection from './QueuedLayerSection';

export interface QueuedSortableViewProps extends BulkAwareProps {
  /** The queued elements (already layer-filtered by the active pill, if any). */
  elements: QueueElement[];
  /** poolElementKey → name of the live element this one replaces on next run. */
  incoming: Map<string, string>;
  campaignId: string;
  provider: QueueProvider;
  /** A search is narrowing the list, so the order on screen is not the whole
   *  layer and must not be written back as one. */
  reorderDisabled?: boolean;
}

const QueuedSortableView: FC<QueuedSortableViewProps> = ({
  elements,
  incoming,
  campaignId,
  provider,
  bulk,
  reorderDisabled = false,
}) => (
  <div className="space-y-3">
    {reorderDisabled ? (
      <p className="px-1 text-[11px] text-muted-foreground">
        Drag-to-reorder is off while a search is active — the order here is the
        promotion order for the whole layer, and only part of it is shown.
      </p>
    ) : null}
    {ALL_LAYERS.map((layer) => {
      const rows = elements
        .filter((e) => e.layer === layer)
        .sort(compareQueueOrder);
      if (!rows.length) return null;
      return (
        <QueuedLayerSection
          key={layer}
          layer={layer}
          elements={rows}
          incoming={incoming}
          campaignId={campaignId}
          provider={provider}
          bulk={bulk}
          reorderDisabled={reorderDisabled}
        />
      );
    })}
  </div>
);

export default QueuedSortableView;
